import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { axiosClient } from '../../../core/axios-client'

const normalizeAxiosErrorPayload = (err) => {
  if (err && typeof err === 'object' && err.response?.data) return err.response.data
  if (err && typeof err === 'object' && (err.detail || err.phone_number || err.code)) return err
  if (typeof err === 'string') return err
  return { detail: err?.message || 'Unknown error' }
}

const fetchUsers = createAsyncThunk(
  'fetchUsers',
  async ({ companyId } = {}) => {
    const resp = await axiosClient.get('/users/', {
      params: companyId ? { company_id: companyId } : {}
    })
    return resp.data.results
  }
)

const fetchUser = createAsyncThunk(
  'fetchUser',
  async ({ id }) => {
    const resp = await axiosClient.get(`/users/${id}`)
    return resp.data.results
  }
)

const fetchMe = createAsyncThunk(
  'fetchMe',
  async () => {
    const resp = await axiosClient.get('/users/me/')
    return resp.data
  }
)

const updateProfile = createAsyncThunk(
  'users/updateProfile',
  async ({ first_name, last_name, patronymic, email, avatar_id }, { rejectWithValue }) => {
    try {
      // Шлём только то, что передали: смена аватара не должна затирать имя
      const payload = {}
      if (first_name !== undefined) payload.first_name = first_name
      if (last_name !== undefined) payload.last_name = last_name
      if (patronymic !== undefined) payload.patronymic = patronymic
      if (email !== undefined) payload.email = email
      if (avatar_id !== undefined) payload.avatar_id = avatar_id
      const resp = await axiosClient.patch('/users/me/', payload)
      return resp.data
    } catch (err) {
      return rejectWithValue(normalizeAxiosErrorPayload(err))
    }
  }
)

const updatePassword = createAsyncThunk(
  'users/updatePassword',
  async ({ old_password, new_password, confirm_password }, { rejectWithValue }) => {
    try {
      const resp = await axiosClient.post('/users/settings/password/', {
        old_password,
        new_password,
        confirm_password,
      })
      return resp.data
    } catch (err) {
      const detail = err.response?.data?.detail
      return rejectWithValue(detail || err.message)
    }
  }
)

const sendPhoneCode = createAsyncThunk(
  'users/sendPhoneCode',
  async ({ phone_number }, { rejectWithValue }) => {
    try {
      const resp = await axiosClient.post('/phone/send-code/', { phone_number })
      return resp.data
    } catch (err) {
      return rejectWithValue(normalizeAxiosErrorPayload(err))
    }
  }
)

const savePhoneAndSendCode = createAsyncThunk(
  'users/savePhoneAndSendCode',
  async ({ phone_number }, { dispatch, rejectWithValue }) => {
    try {
      await axiosClient.patch('/users/me/', { phone_number })

      await dispatch(sendPhoneCode({ phone_number })).unwrap()

      dispatch(fetchMe())

      return { phone_number }
    } catch (err) {
      return rejectWithValue(normalizeAxiosErrorPayload(err))
    }
  }
)

const verifyPhoneCode = createAsyncThunk(
  'users/verifyPhoneCode',
  async ({ phone_number, code }, { dispatch, rejectWithValue }) => {
    try {
      const resp = await axiosClient.post('/phone/verify-code/', { phone_number, code })
      dispatch(fetchMe())
      return resp.data
    } catch (err) {
      return rejectWithValue(normalizeAxiosErrorPayload(err))
    }
  }
)

const sendEmailCode = createAsyncThunk(
  'users/sendEmailCode',
  async (_, { rejectWithValue }) => {
    try {
      const resp = await axiosClient.post('/email/send-code/')
      return resp.data
    } catch (err) {
      return rejectWithValue(normalizeAxiosErrorPayload(err))
    }
  }
)

const verifyEmailCode = createAsyncThunk(
  'users/verifyEmailCode',
  async ({ code }, { dispatch, rejectWithValue }) => {
    try {
      const resp = await axiosClient.post('/email/verify-code/', { code })
      dispatch(fetchMe())
      return resp.data
    } catch (err) {
      return rejectWithValue(normalizeAxiosErrorPayload(err))
    }
  }
)

const initialState = {
  users: [],
  user: null,
  currentUser: null,
  updatingPassword: false,
  updatingProfile: false,
  phoneLoading: false,
  emailLoading: false,
}

export const slice = createSlice({
  name: 'users',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchUsers.fulfilled, (state, { payload }) => {
      state.users = payload
    })

    builder.addCase(fetchUser.fulfilled, (state, { payload }) => {
      state.user = payload
    })

    builder.addCase(fetchMe.fulfilled, (state, { payload }) => {
      state.currentUser = payload
    })

    builder
      .addCase(updateProfile.pending, (state) => {
        state.updatingProfile = true
      })
      .addCase(updateProfile.fulfilled, (state, { payload }) => {
        state.updatingProfile = false
        state.currentUser = payload
      })
      .addCase(updateProfile.rejected, (state) => {
        state.updatingProfile = false
      })

    builder
      .addCase(updatePassword.pending, (state) => {
        state.updatingPassword = true
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.updatingPassword = false
      })
      .addCase(updatePassword.rejected, (state) => {
        state.updatingPassword = false
      })

    builder
      .addCase(sendPhoneCode.pending, (state) => {
        state.phoneLoading = true
      })
      .addCase(sendPhoneCode.fulfilled, (state) => {
        state.phoneLoading = false
      })
      .addCase(sendPhoneCode.rejected, (state) => {
        state.phoneLoading = false
      })

    builder
      .addCase(savePhoneAndSendCode.pending, (state) => {
        state.phoneLoading = true
      })
      .addCase(savePhoneAndSendCode.fulfilled, (state) => {
        state.phoneLoading = false
      })
      .addCase(savePhoneAndSendCode.rejected, (state) => {
        state.phoneLoading = false
      })

    builder
      .addCase(verifyPhoneCode.pending, (state) => {
        state.phoneLoading = true
      })
      .addCase(verifyPhoneCode.fulfilled, (state) => {
        state.phoneLoading = false
      })
      .addCase(verifyPhoneCode.rejected, (state) => {
        state.phoneLoading = false
      })

    builder
      .addCase(sendEmailCode.pending, (state) => {
        state.emailLoading = true
      })
      .addCase(sendEmailCode.fulfilled, (state) => {
        state.emailLoading = false
      })
      .addCase(sendEmailCode.rejected, (state) => {
        state.emailLoading = false
      })

    builder
      .addCase(verifyEmailCode.pending, (state) => {
        state.emailLoading = true
      })
      .addCase(verifyEmailCode.fulfilled, (state) => {
        state.emailLoading = false
      })
      .addCase(verifyEmailCode.rejected, (state) => {
        state.emailLoading = false
      })
  },
})

export {
  fetchUsers,
  fetchUser,
  fetchMe,
  updateProfile,
  updatePassword,
  sendPhoneCode,
  savePhoneAndSendCode,
  verifyPhoneCode,
  sendEmailCode,
  verifyEmailCode,
}

export default slice.reducer
